import React, { useState, useEffect } from 'react';
import '../css/CreativeTokens.css';
import c from '../Images/creativetokens.png'
import revision from '../Images/creativetokens/revision.png'

const CreativeTokens = () => {
 const [selectedRegion, setSelectedRegion] = useState('US');
 const [scrollY, setScrollY] = useState(0);

 // Scroll effect for hero zoom
 useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };
  
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

 // Calculate zoom scale - starts zoomed in (1.2) and zooms out to normal (1.0)
 const zoomScale = Math.max(0.1, 1.2 - (scrollY * 0.0009));

 const pricingData = {
   US: {
     currency: '$',
     prices: [70, 130, 250],
     addons: {
       videoRevisions: 2,
       fasterDelivery: 25,
       additionalMinute: 4,
       animatedLogo: 25,
       brandedMusic: 35,
       profileDesign: 12,
       businessCard: 60,
       landingPage: 120
     }
   },
   Europe: {
     currency: '€',
     prices: [65, 120, 230],
     addons: {
       videoRevisions: 2,
       fasterDelivery: 23,
       additionalMinute: 4,
       animatedLogo: 23,
       brandedMusic: 32,
       profileDesign: 11,
       businessCard: 55,
       landingPage: 110
     }
   },
   India: {
     currency: '₹',
     prices: [5800, 10800, 20800],
     addons: {
       videoRevisions: 160,
       fasterDelivery: 2080,
       additionalMinute: 330,
       animatedLogo: 2080,
       brandedMusic: 2900,
       profileDesign: 1000,
       businessCard: 4980,
       landingPage: 9980
     }
   }
 };

 const currentPricing = pricingData[selectedRegion];

 return (
   <div className="creative-container">
     
     {/* Hero Section with Zoom Effect */}
     <section 
       className="creative-hero"
       style={{
         transform: `matrix(${zoomScale}, 0, 0, ${zoomScale}, 0, 0)`
       }}
     >
       <div className="creative-hero-content">
         <h1>Creative Tokens</h1>
         <p>
           Pay once. Use anytime. Creative Tokens are your flexible, on-demand credits for accessing quick designs, micro tasks, and creative services—without any subscription.
         </p>
         <div className="creative-buttons">
           <button className="primary-btn">Get Tokens</button>
           <button className="secondary-btn">Contact Us</button>
         </div>
       </div>
     </section>

     {/* How It Works */}
     <section className="how-it-works">
       <h2>How It Works</h2>
       <div className="steps">
         <div className="step">
           <div className="step-number">1</div>
           <h3>Buy Tokens</h3>
           <p>Choose the quantity based on your needs. Simple, upfront pricing.</p>
         </div>
         <div className="step">
           <div className="step-number">2</div>
           <h3>Submit a Brief</h3>
           <p>Share your creative requirement—whether it's design, assets, or quick tasks.</p>
         </div>
         <div className="step">
           <div className="step-number">3</div>
           <h3>Get it Delivered</h3>
           <p>Our team delivers high-quality work, quickly and hassle-free.</p>
         </div>
       </div>
     </section>

     {/* Token Usage */}
     <section className="usage-section">
       <h2>What Can You Use Tokens For?</h2>
       <div className="usage-grid">
         <div className="usage-card">Logo Adjustments</div>
         <div className="usage-card">Social Media Posts</div>
         <div className="usage-card">Banners & Posters</div>
         <div className="usage-card">Pitch Deck Slides</div>
         <div className="usage-card">UI/UX Wireframes</div>
         <div className="usage-card">Brand Mockups</div>
         <div className="usage-card">Packaging Tweaks</div>
         <div className="usage-card">Micro Video Graphics</div>
         <div className="usage-card">Idea Validation</div>
       </div>
     </section>

     {/* Updated Token Pricing */}
     <section className="token-pricing">
       <h2>Creative Tokens Pricing</h2>
       
       <div className="region-selector">
         <div className="region-tabs">
           {Object.keys(pricingData).map((region) => (
             <button
               key={region}
               className={`region-tab ${selectedRegion === region ? 'active' : ''}`}
               onClick={() => setSelectedRegion(region)}
             >
               {region === 'US' ? '🇺🇸 United States' : 
                region === 'Europe' ? '🇪🇺 Europe' : 
                '🇮🇳 India'}
             </button>
           ))}
         </div>
       </div>

       <div className="pricing-cards">
         <div className="pricing-card essential">
           <div className="pricing-badge">Essential</div>
           <div className="token-circle">
             <div className="token-icon"></div>
             <span className="token-count">50</span>
           </div>
           <div className="price-display">{currentPricing.currency}{currentPricing.prices[0]}</div>
         </div>
         <div className="pricing-card popular">
           <div className="pricing-badge">Popular</div>
           <div className="token-circle">
             <div className="token-icon"></div>
             <span className="token-count">120</span>
           </div>
           <div className="price-display">{currentPricing.currency}{currentPricing.prices[1]}</div>
         </div>
         <div className="pricing-card best-value">
           <div className="pricing-badge">Best Value</div>
           <div className="token-circle">
             <div className="token-icon"></div>
             <span className="token-count">250</span>
           </div>
           <div className="price-display">{currentPricing.currency}{currentPricing.prices[2]}</div>
         </div>
       </div>
     </section>

     {/* Usage for Graphic Design */}
     <section className="graphic-design-usage">
       <h2>Usage for Graphic Design:</h2>
       <div className="design-tiers">
         <div className="design-tier">
           <h3>Basic (Canva, Express)</h3>
           <div className="usage-items">
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>5 tokens = 1 Poster</span>
             </div>
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>1 token = 1 Revision</span>
             </div>
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>2 tokens = 1 Fast Delivery</span>
             </div>
           </div>
           <div className="tier-description">
             Affordable and quicker, ideal for those who need visually appealing yet simple designs with minimal custom work.
           </div>
         </div>
         <div className="design-tier">
           <h3>Pro (Adobe, Corel, Affinity)</h3>
           <div className="usage-items">
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>20 tokens = 1 Poster</span>
             </div>
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>10 tokens = 1 Revision</span>
             </div>
             <div className="usage-item">
               <div className="token-mini"></div>
               <span>3 tokens = 1 Fast Delivery</span>
             </div>
           </div>
           <div className="tier-description">
             Suited for high-end, professional-grade projects requiring custom graphics and scalability, at a higher cost due to the expertise.
           </div>
         </div>
       </div>
     </section>

     {/* Add-ons Section */}
     <section className="addons-section">
       <h2>Add ons for solutions through creative tokens</h2>
       <div className="addons-grid">
         <div className="addon-card">
           <div className="addon-icon video-icon">
            <img src={revision}></img>
           </div>
           <h3>Video Revisions</h3>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>1</span>
             <small>Onwards</small>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.videoRevisions}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon speed-icon">⚡</div>
           <h3>Faster Delivery</h3>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>20</span>
             <small>Onwards</small>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.fasterDelivery}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon time-icon">📊</div>
           <h3>Additional Minute</h3>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>3</span>
             <small>Onwards</small>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.additionalMinute}</div>
         </div>
         <div className="addon-card special">
           <div className="addon-icon logo-icon">🔄</div>
           <h3>Animated Logo</h3>
           <div className="discount-badge">Flat 10% OFF on Final price</div>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>20</span>
             <small>Onwards</small>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.animatedLogo}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon music-icon">🎵</div>
           <h3>Branded Music</h3>
           <small>(30 sec)</small>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>30</span>
             <small>Onwards</small>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.brandedMusic}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon profile-icon">👤</div>
           <h3>Profile Design</h3>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>10</span>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.profileDesign}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon card-icon">💼</div>
           <h3>Business Card</h3>
           <small>(2 variations)</small>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>50</span>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.businessCard}</div>
         </div>
         <div className="addon-card">
           <div className="addon-icon page-icon">🌐</div>
           <h3>Landing Page</h3>
           <small>Only Design</small>
           <div className="addon-price">
             <div className="token-mini"></div>
             <span>100</span>
           </div>
           <div className="addon-cost">{currentPricing.currency}{currentPricing.addons.landingPage}</div>
         </div>
       </div>
     </section>

     {/* What Are Tokens */}
     <section className="about-tokens">
       <h2>Why use this new way?</h2>
       <p>
         Creative Tokens are a prepaid service currency designed by <strong>Red Fibre</strong> to simplify your creative workflow. 
         Whether it's a quick edit, a social post, or a design tweak, tokens make it seamless—no invoices, no contracts, just pure creativity on demand.
       </p>
       <p>
         Ideal for both existing and new clients, tokens also serve as loyalty rewards when purchasing services beyond regular scope.
       </p>
       <div className="note-box">
         <strong>Note:</strong> Tokens are not part of a subscription. Buy once, redeem anytime.
       </div>
     </section>

     {/* Call to Action */}
     <section className="cta-section">
       <h2>Ready to Get Started?</h2>
       <p>Fuel your brand with Creative Tokens. One-time purchase. Lifetime flexibility.</p>
       <button className="primary-btn">Get Tokens Now</button>
     </section>

   </div>
 );
};

export default CreativeTokens;